import { FlexTypography } from "@lyfegen-platform/react-common";
import { Box, Typography } from "@material-ui/core";
import { TFunction, useTranslation } from "react-i18next";
import FlexTypographyComponent from "../../../../components/FlexTypographyComponent";
import FormattedCounterparties from "../../../../components/FormattedCounterparties";
import { tableIdMinWidth } from "../../../../hooks/use-map-theme-styles";
import useResizeObserver from "../../../../hooks/use-resize-observer";
import { DocumentStatus } from "../../../../models/generated/graphql";
import { Column } from "../../../../types/tableTypes";
import renderCurrency from "../../../../utils/currency";
import { localeDate } from "../../../../utils/locale-date";
import { getTagTranslationKey } from "../../../agreements/AgreementsTable";
import IdColumn from "../../refunds/columns/IdColumn";
import ProductColumn from "../../refunds/columns/ProductColumn";
import { GetDocumentsQuery } from "../types/queries.generated";
import DocumentStatusComponent from "./DocumentStatusComponent";
import Adjust from "apps/platform-client/client/src/components/icons/Adjust";
import LyfeTooltip from "apps/platform-client/client/src/components/LyfeTooltip";
import { EMPTY_VALUE_PLACEHOLDER } from "apps/platform-client/client/src/models/common/data";

type DocumentColumn = Column<GetDocumentsQuery["documentTableRows"][number]>;

const DocumentTypeComponent: React.FC<{ rawData: any; fieldAcessor: string; t: TFunction }> = (props) => {
	const { ref, width = 1 } = useResizeObserver<HTMLDivElement>();

	return (
		<div ref={ref}>
			<FlexTypography width={width} tooltipPlacement="top-start" text={props.t(props.rawData.type)} />
		</div>
	);
};

export const getDocumentColumns = (t: TFunction<string>): Array<DocumentColumn> => [
	{
		title: t("documentId"),
		field: "id",
		default: true,
		render: (rawData) => <IdColumn entityId={rawData.id} />,
	},
	{
		title: t("smartMipReimbursementId"),
		field: "smartMipReimbursementId",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={rawData.smartMipReimbursementId || EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("type"),
		field: "type",
		default: true,
		render: (rawData) => <DocumentTypeComponent rawData={rawData} fieldAcessor="type" t={t} />,
	},
	{
		title: t("status"),
		field: "status",
		default: true,
		render: (rawData) => {
			if (rawData.status === DocumentStatus.InReview || rawData.status === DocumentStatus.InApproval) {
				return (
					<DocumentStatusComponent
						status={rawData.status!}
						text={`${t(rawData.status === DocumentStatus.InReview ? "in-review-of" : "in-approval-of")} ${
							rawData.currentReviewerOrganization || ""
						}`}
					/>
				);
			}
			return <DocumentStatusComponent status={rawData.status!} text={t(rawData.status || "")} />;
		},
	},
	{
		title: t("period"),
		field: "coveragePeriod",
		default: true,
		align: "right",
		render: (rowData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={`${localeDate(rowData.coveragePeriod!.split(" - ")[0])}-${localeDate(rowData.coveragePeriod!.split(" - ")[1])}`}
			/>
		),
	},
	{
		title: t("counter-parties"),
		field: "counterParties",
		default: true,
		render: (rawData) => <FormattedCounterparties rowData={rawData} fieldAssessor="counterParties" />,
	},
	{
		title: t("agreement-tags"),
		field: "formattedTags",
		default: true,
		render: (rawData) => (
			<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
				{t(getTagTranslationKey(rawData.formattedTags!))}
			</Typography>
		),
	},
	{ title: t("product"), field: "product", default: true, render: (rawData: any) => <ProductColumn product={rawData.product} /> },
	{
		title: t("document-beneficiary"),
		field: "beneficiary",
		default: false,
		render: (rawData) => (
			<Typography variant="body2">
				{rawData.beneficiary ? t(`document-beneficiary-${rawData.beneficiary.toLowerCase()}`) : EMPTY_VALUE_PLACEHOLDER}
			</Typography>
		),
	},
	{
		title: t("document-payment-reference-lyfegen"),
		field: "paymentReferenceLyfegen",
		default: false,
		render: (rawData) => <Typography variant="body2">{rawData.paymentReferenceLyfegen || EMPTY_VALUE_PLACEHOLDER}</Typography>,
	},
	{
		title: t("document-payment-receipt-payer"),
		field: "paymentReceiptPayer",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={!!rawData.paymentReceiptPayer ? localeDate(new Date(rawData.paymentReceiptPayer)) : EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("created-on"),
		field: "createdOn",
		default: true,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent containerStyles={{ justifyContent: "flex-end" }} text={localeDate(new Date(rawData.createdOn))!} />
		),
	},
	{
		title: t("sent-to-review-on"),
		field: "sentToReviewOn",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={!!rawData.sentToReviewOn ? localeDate(new Date(rawData.sentToReviewOn)) : EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("review-reminded-on"),
		field: "reviewRemindedOn",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={!!rawData.reviewRemindedOn ? localeDate(new Date(rawData.reviewRemindedOn)) : EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("issued-on"),
		field: "issuedOn",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={!!rawData.issuedOn ? localeDate(new Date(rawData.issuedOn)) : EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("last-reminded-on"),
		field: "lastRemindedOn",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={!!rawData.lastRemindedOn ? localeDate(new Date(rawData.lastRemindedOn)) : EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
	{
		title: t("invoice-amount"),
		field: "finalInvoiceAmount",
		default: true,
		align: "right",
		render: (rawData) => {
			const isDocumentRestricted = !!rawData.agreementWithoutUserAccessNames?.length;
			const amountToDisplay = !!rawData.adjustedInvoiceAmount ? rawData.adjustedInvoiceAmount : rawData.invoiceAmount;
			const text = !isDocumentRestricted ? renderCurrency(amountToDisplay || 0, rawData.currency, 2, 2) : `XX ${rawData.currency}`;

			return (!!rawData.adjustedInvoiceAmount || !!rawData.hasAdjustedTransactions) && !isDocumentRestricted ? (
				<LyfeTooltip title={t("adjusted-amount-total-hover")}>
					<Box justifyContent={"space-between"} display="flex" alignItems="center">
						<span style={{ lineHeight: "12px" }}>{<Adjust fill="#212121" />}</span>
						<span
							style={{
								maxWidth: 300,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								fontWeight: 600,
							}}
						>
							{text}
						</span>
					</Box>
				</LyfeTooltip>
			) : (
				<FlexTypographyComponent
					forceTooltipRender
					containerStyles={{ justifyContent: "flex-end" }}
					renderTooltipText={() =>
						renderCurrency(
							!isDocumentRestricted ? amountToDisplay : "XX",
							(t(rawData.currency || "") || "") + ((amountToDisplay || 0) !== 1 ? "s" : ""),
							2,
							2
						)
					}
					text={text}
				/>
			);
		},
	},
	{
		title: t("approvers"),
		field: "formattedApprovers",
		default: false,
		align: "right",
		render: (rawData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={rawData.formattedApprovers || EMPTY_VALUE_PLACEHOLDER}
			/>
		),
	},
];

const getObjectPropertyValue = (entry: any, t: TFunction<string>): string => {
	const [prop, val] = entry;
	switch (prop) {
		case "contractType":
			return t(val);
		default:
			return String(val);
	}
};

const propertyContainsSearchTerm = (input: string, t: TFunction<string>) => (o: any) => {
	const val = getObjectPropertyValue(o, t).toLowerCase();
	const result = val.includes(input);
	return result;
};

export const searchDocument = (input: string, t: TFunction<string>) => (entry: any) => {
	const i = input.toLowerCase();
	return Object.entries(entry).some(propertyContainsSearchTerm(i, t));
};

export default function useDocumentColumns(): { columns: Array<DocumentColumn> } {
	const { t } = useTranslation();
	const columns = getDocumentColumns(t as any);

	return { columns };
}
